import { InputEvent } from "react";
import { shallowEqual } from "react-redux";

// Spark components
import { GuxFormFieldNumber } from "genesys-spark-components-react";

// Cloud frontend lib
import {
    AssessmentFormQuestionTypeEnum,
    SurveyQuestionTypeEnum,
} from "@genesys/cloud-frontend/dist/mjs/models";

// common
import { DefaultLimits } from "constants/DefaultLimits";
import {
    invalidIntegerInputCharacters,
    selectIsQuestionHavingValidMaxResponseChar,
    selectIsQuestionValidForNADefaultAnswer,
} from "features/qualityForm/qualityFormValidations";
import { useStrings } from "features/i18n/useStrings";
import FormType from "enums/FormType";
import { useAppDispatch, useAppSelector } from "app/hooks";

// feature slice
import { qualityFormActions } from "features/qualityForm/qualityFormSlice";
import {
    selectQuestion,
    selectIsAutomatedEvaluationForm,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";

// Question common
import { Property, PropertySection } from "../common/PropertySection";

// Siblings
import strings from "./AnswerPropertiesSection.strings";
import { ErrorStatement } from "../common/ErrorStatement/ErrorStatement";

export default function AnswerPropertiesSection({
    questionId,
    formType,
    numberInputRef,
}: {
    questionId: string;
    formType?: FormType;
    numberInputRef: React.RefObject<HTMLInputElement> | null;
}) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);
    const { questionType, commentsRequired, naEnabled, maxResponseCharacters } =
        useAppSelector((state) => {
            const question = selectQuestion(state, questionId);
            return {
                questionType: question?.type,
                commentsRequired: question?.commentsRequired,
                naEnabled: question?.naEnabled,
                maxResponseCharacters: question?.maxResponseCharacters,
            };
        }, shallowEqual);

    // keep this a separate selector as it can be frequently updated
    const isMaxResponseCharSizeInvalid = useAppSelector(
        (state) =>
            !selectIsQuestionHavingValidMaxResponseChar(state, questionId)
    );

    const isAutomatedEvaluationForm = useAppSelector(
        selectIsAutomatedEvaluationForm
    );

    const isFreeTextQuestion =
        questionType === SurveyQuestionTypeEnum.FreeTextQuestion ||
        questionType === AssessmentFormQuestionTypeEnum.FreeTextQuestion;

    const showRequireComments =
        formType !== FormType.SURVEY &&
        questionType !== AssessmentFormQuestionTypeEnum.FreeTextQuestion &&
        !isAutomatedEvaluationForm;

    const showQuestionNAEnabledRequiredError = useAppSelector(
        (state) => !selectIsQuestionValidForNADefaultAnswer(state, questionId)
    );

    return (
        <PropertySection title={t.labels.title}>
            <div data-testid="answer-properties-section">
                {showRequireComments && (
                    <Property
                        name="requireComments"
                        checked={commentsRequired}
                        label={t.labels.requireComments}
                        onChange={() => {
                            dispatch(
                                qualityFormActions.changeQuestionCommentsRequired(
                                    {
                                        questionId: questionId,
                                    }
                                )
                            );
                        }}
                    />
                )}
                <Property
                    name="addNA"
                    checked={naEnabled}
                    onChange={() => {
                        dispatch(
                            qualityFormActions.setNAEnabledQuestion({
                                questionId: questionId,
                                value: !naEnabled,
                            })
                        );
                    }}
                    label={
                        isFreeTextQuestion
                            ? t.labels.freeText.optionalCheck
                            : t.labels.addNA
                    }
                />
                {isFreeTextQuestion && (
                    <GuxFormFieldNumber
                        labelPosition="above"
                        className="free-text-max-characters"
                    >
                        <input
                            slot="input"
                            name="number"
                            type="number"
                            ref={numberInputRef}
                            step="1"
                            min={DefaultLimits.minResponseCharacters}
                            max={DefaultLimits.maxResponseCharacters}
                            value={maxResponseCharacters}
                            onBeforeInput={(
                                event: InputEvent<HTMLInputElement>
                            ) => {
                                if (
                                    invalidIntegerInputCharacters.includes(
                                        event.data
                                    )
                                ) {
                                    event.preventDefault();
                                }
                            }}
                            onChange={({ currentTarget }) => {
                                dispatch(
                                    qualityFormActions.setMaxResponseCharacters(
                                        {
                                            questionId,
                                            maxResponseCharacters:
                                                currentTarget.value,
                                        }
                                    )
                                );
                            }}
                        />
                        <label slot="label">
                            {t.labels.nps.maximumResponseCharacters}
                        </label>
                        {isMaxResponseCharSizeInvalid && (
                            <span slot="error" role="alert">
                                {t.errors.maxResponseCharsInvalid({
                                    maxResponseCharacters:
                                        DefaultLimits.maxResponseCharacters,
                                })}
                            </span>
                        )}
                    </GuxFormFieldNumber>
                )}
            </div>
            {showQuestionNAEnabledRequiredError && (
                <div
                    className="question-na-enabled-required-error"
                    data-testid="question-na-enabled-required-error"
                >
                    <ErrorStatement
                        errorMessage={t.errors.naEnabledRequiredForQuestion}
                    />
                </div>
            )}
        </PropertySection>
    );
}
